import { Controller } from "@hotwired/stimulus"

/**
 * Swipe Back Controller
 *
 * The iOS "swipe from the left edge to go back" for panels stacked on top of
 * each other. The panel follows the finger; let go past the threshold (or
 * with a flick) and it finishes the way out, otherwise it settles back.
 *
 * The controller moves nothing itself. It sets --swipe-progress (0 = where
 * the panel is, 1 = fully off to the right), --swipe-duration and the swiping
 * class on the panel's container, and the container's CSS turns those into
 * transforms — so the drag starts and ends exactly where the class states put
 * the panels, and nothing jumps when the two hand over.
 *
 * Usage:
 *   <div data-controller="swipe-back"
 *        data-swipe-back-swiping-class-value="is-swiping-room">
 *     <!-- panel content -->
 *   </div>
 *
 * The container is the closest `.bryght-chat-container`. The gesture is
 * limited to touch/pen, to the stacked layout (`--chat-stacked: 1` on the
 * panel), to touches that start within `edge` px of the panel's left
 * side, and to the panel that is on top (the innermost swipe-back element
 * under the touch).
 *
 * In the Hotwire Native app the gesture is only available when the app
 * hands its own edge-swipe over to us (the `chat-navigation` bridge
 * component is registered); an older app keeps its native swipe alone.
 *
 * Events dispatched (bubbles: true):
 *   - "swipe-back:start"  - the drag has begun
 *   - "swipe-back:commit" - the panel is fully off-screen; the listener must
 *                           switch the state without animating, the drag
 *                           geometry is removed right after the event
 *   - "swipe-back:cancel" - the panel has settled back
 */

const CONTAINER_SELECTOR = ".bryght-chat-container"
const PANEL_SELECTOR = "[data-controller~='swipe-back']"
const BRIDGE_COMPONENT = "chat-navigation"

// Movement needed before the gesture decides between swipe and scroll.
const INTENT_PX = 8
// Longest the settle after letting go may take.
const SETTLE_MAX_MS = 250
const SETTLE_MIN_MS = 80
const CANCEL_MS = 200
// A transitionend that never comes (styles missing, transitions disabled).
const SETTLE_FALLBACK_MS = 400

// True when `event` (a pointerdown) starts inside the edge zone of a
// swipe-back panel that would take the gesture. Other horizontal gestures
// (swipe to reply) leave those touches alone.
export function startsInSwipeBackEdge(event) {
  const panel = event.target.closest?.(PANEL_SELECTOR)
  if (!panel) return false
  const controller = panel.swipeBackController
  return Boolean(controller && controller.wouldStart(event))
}

export default class extends Controller {
  static values = {
    swipingClass: String,
    edge:         { type: Number, default: 40 },   // px from the panel's left side
    threshold:    { type: Number, default: 0.35 }, // share of the width to commit
    velocity:     { type: Number, default: 0.5 },  // px/ms to commit with a flick
  }

  connect() {
    this.element.swipeBackController = this
    this.reset()

    this._onPointerDown = this.onPointerDown.bind(this)
    this._onPointerMove = this.onPointerMove.bind(this)
    this._onPointerUp = this.onPointerUp.bind(this)
    this._onDragStart = this.onDragStart.bind(this)
    this._onClick = this.onClick.bind(this)

    this.element.addEventListener("pointerdown", this._onPointerDown, { passive: true })
    this.element.addEventListener("pointermove", this._onPointerMove, { passive: true })
    this.element.addEventListener("pointerup", this._onPointerUp, { passive: true })
    this.element.addEventListener("pointercancel", this._onPointerUp, { passive: true })
    this.element.addEventListener("dragstart", this._onDragStart)
    this.element.addEventListener("click", this._onClick, { capture: true })
  }

  disconnect() {
    this.element.removeEventListener("pointerdown", this._onPointerDown)
    this.element.removeEventListener("pointermove", this._onPointerMove)
    this.element.removeEventListener("pointerup", this._onPointerUp)
    this.element.removeEventListener("pointercancel", this._onPointerUp)
    this.element.removeEventListener("dragstart", this._onDragStart)
    this.element.removeEventListener("click", this._onClick, { capture: true })
    this.clearSettle()
    if (this.swiping) this.clearDrag()
    delete this.element.swipeBackController
  }

  reset() {
    this.pointerId = null
    this.startX = 0
    this.startY = 0
    this.progress = 0
    this.swiping = false
    this.scrolling = false
    this.samples = []
  }

  get container() {
    return this.element.closest(CONTAINER_SELECTOR)
  }

  // Whether a pointerdown would be taken by this panel.
  wouldStart(event) {
    if (event.pointerType === "mouse") return false
    if (this.settling) return false

    if (!this.container) return false
    if (getComputedStyle(this.element).getPropertyValue("--chat-stacked").trim() !== "1") return false
    if (!nativeHandsOverSwipe()) return false

    // The topmost panel only: the media gallery sits inside the details panel.
    if (event.target.closest(PANEL_SELECTOR) !== this.element) return false

    const left = this.element.getBoundingClientRect().left
    if (event.clientX - left > this.edgeValue) return false

    // Something that scrolls sideways keeps its own drag.
    if (scrollsHorizontally(event.target, this.element)) return false

    return true
  }

  onPointerDown(event) {
    if (this.pointerId !== null) return
    if (!this.wouldStart(event)) return

    this.pointerId = event.pointerId
    this.startX = event.clientX
    this.startY = event.clientY
    this.progress = 0
    this.swiping = false
    this.scrolling = false
    this.samples = [{ x: event.clientX, t: event.timeStamp }]
  }

  onPointerMove(event) {
    if (event.pointerId !== this.pointerId || this.scrolling) return

    const dx = event.clientX - this.startX
    const dy = event.clientY - this.startY

    if (!this.swiping) {
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > INTENT_PX) {
        this.scrolling = true
        return
      }
      if (dx < INTENT_PX) return
      this.startDrag(event)
    }

    this.samples.push({ x: event.clientX, t: event.timeStamp })
    if (this.samples.length > 5) this.samples.shift()

    this.progress = clamp(dx / this.element.offsetWidth, 0, 1)
    this.setDragProperty("--swipe-progress", this.progress)
  }

  onPointerUp(event) {
    if (event.pointerId !== this.pointerId) return
    const wasSwiping = this.swiping
    const cancelled = event.type === "pointercancel"
    this.pointerId = null

    if (!wasSwiping) return

    try { this.element.releasePointerCapture(event.pointerId) } catch (_) {}

    // The tap that ends the drag must not click what is under the finger.
    this.suppressClick = true
    setTimeout(() => { this.suppressClick = false }, 0)

    // The browser (or the native app) took the pointer for itself: never commit.
    const commit = !cancelled && (this.progress > this.thresholdValue || this.flickVelocity() > this.velocityValue)
    this.settle(commit)
  }

  startDrag(event) {
    this.swiping = true
    try { this.element.setPointerCapture(event.pointerId) } catch (_) {}

    // Kept for the whole gesture: a committed panel may leave the document
    // before its drag geometry is cleaned up (the media gallery is removed).
    this.activeContainer = this.container
    this.setDragProperty("--swipe-duration", "0s")
    this.setDragProperty("--swipe-progress", "0")
    this.activeContainer.classList.add(this.swipingClassValue)
    this.dispatch("start", { bubbles: true })
  }

  setDragProperty(name, value) {
    this.activeContainer?.style.setProperty(name, String(value))
  }

  // Finishes the drag: out of view (commit) or back to where it was (cancel).
  settle(commit) {
    const target = commit ? 1 : 0
    const remaining = Math.abs(target - this.progress)
    const duration = commit
      ? clamp(remaining * SETTLE_MAX_MS, SETTLE_MIN_MS, SETTLE_MAX_MS)
      : CANCEL_MS

    this.settling = true
    const finish = () => {
      this.clearSettle()
      this.settling = false
      if (commit) {
        // The listener switches the class state first (without animating);
        // only then may the drag geometry go, or the panel would flash back.
        this.dispatch("commit", { bubbles: true })
      } else {
        this.dispatch("cancel", { bubbles: true })
      }
      this.clearDrag()
    }

    // Already there: nothing to wait for.
    if (remaining === 0) {
      finish()
      return
    }

    this._onTransitionEnd = (e) => {
      if (e.target === this.element && e.propertyName === "transform") finish()
    }
    this.element.addEventListener("transitionend", this._onTransitionEnd)
    this._settleTimer = setTimeout(finish, duration + SETTLE_FALLBACK_MS)

    this.setDragProperty("--swipe-duration", `${Math.round(duration)}ms`)
    this.setDragProperty("--swipe-progress", target)
  }

  clearSettle() {
    if (this._onTransitionEnd) {
      this.element.removeEventListener("transitionend", this._onTransitionEnd)
      this._onTransitionEnd = null
    }
    if (this._settleTimer) {
      clearTimeout(this._settleTimer)
      this._settleTimer = null
    }
  }

  clearDrag() {
    const container = this.activeContainer
    if (container) {
      container.classList.remove(this.swipingClassValue)
      container.style.removeProperty("--swipe-progress")
      container.style.removeProperty("--swipe-duration")
    }
    this.activeContainer = null
    this.reset()
  }

  // px/ms over the last few moves, positive to the right.
  flickVelocity() {
    const samples = this.samples
    if (samples.length < 2) return 0
    const first = samples[0]
    const last = samples[samples.length - 1]
    const dt = last.t - first.t
    if (dt <= 0) return 0
    return (last.x - first.x) / dt
  }

  onClick(event) {
    if (!this.suppressClick) return
    event.preventDefault()
    event.stopPropagation()
    this.suppressClick = false
  }

  onDragStart(event) {
    if (this.pointerId !== null) event.preventDefault()
  }
}

// In the Hotwire Native app the edge belongs to the native back swipe unless
// the app registered the chat-navigation bridge component, which lets the
// web take over while a chat panel is open. Outside the app there is nothing
// to hand over.
function nativeHandsOverSwipe() {
  const inNativeApp = document.body.classList.contains("hotwire-native-app")
    || Boolean(document.documentElement.dataset.bridgePlatform)
  if (!inNativeApp) return true

  // The bridge lists the app's components on <html> once its adapter is in;
  // the app's user agent carries the same list from the first byte.
  const { bridgeComponents } = document.documentElement.dataset
  if ((bridgeComponents || "").split(" ").includes(BRIDGE_COMPONENT)) return true
  const fromUserAgent = navigator.userAgent.match(/bridge-components: \[(.*?)\]/)
  return Boolean(fromUserAgent && fromUserAgent[1].split(" ").includes(BRIDGE_COMPONENT))
}

function scrollsHorizontally(target, panel) {
  for (let el = target; el && el !== panel; el = el.parentElement) {
    const overflowX = getComputedStyle(el).overflowX
    if ((overflowX === "auto" || overflowX === "scroll") && el.scrollWidth > el.clientWidth) {
      return true
    }
  }
  return false
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}
